"use client";
import React, { useState, useCallback, useEffect, useRef } from "react";
import styles from "./header-opt.module.scss";
import Image from "next/image";
import Link from "next/link";
import Close from "@/SvgIcons/Close";
import ClientSideButton from "@/components/molecules/Carousels/HeroCarousel/ClientSideButton";
import { ModalTypes } from "@/types/modal";
import Hamburger from "@/SvgIcons/Hamburger";
import { usePathname } from "next/navigation";
import MobileMenuOpt from "./MobileMenuOpt";
import headerMenuData from "@/data/header-menu-data.json";
import popupData from "@/data/popup-data.json";
import { GA_EVENTS, GAEvent } from "@/helpers/gaEvents";
import ProgramSearcher from "./ProgramSearcher";
import ProgramSearchResults from "./ProgramSearchResults";
interface HeaderOptProps {
  isSponsoredCertificates?: boolean;
}

const HeaderOpt: React.FC<HeaderOptProps> = ({
  isSponsoredCertificates = false,
}) => {
  const { LEAD_INITIATE } = GAEvent;
  const [menuPage, setMenuPage] = useState<number>(0);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const pathname = usePathname();
  const [isStickyHeader, setIsStickyHeader] = useState<boolean>(false);
  const lastScrollY = useRef<number>(0);
  const isCertificationsPage = pathname == "/certifications/thank-you" || pathname == "/certifications/payment-failed" || pathname == "/certifications";

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY < lastScrollY.current && currentScrollY > 108) {
        setIsStickyHeader(true);
      } else {
        setIsStickyHeader(false);
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (isMenuOpen || searchValue) {
      document.body.classList.add("scroll-lock");
    } else {
      document.body.classList.remove("scroll-lock");
    }

    return () => {
      document.body.classList.remove("scroll-lock");
    };
  }, [isMenuOpen,searchValue]);

  useEffect(() => {
    setIsMenuOpen(false);
    setMenuPage(0);
  }, [pathname]);

  const toggleMenu = useCallback((): void => {
    setSearchValue("")
    setIsMenuOpen((prev) => !prev);
  }, []);

  return (
     <>
    <header
      className={`${styles.header} ${isMenuOpen ? styles.open : ""} ${
        isStickyHeader ? styles.sticky : ""
      }`}
    >
      <div className={styles.headerWrapper}>
        <Link href="/" target="_self" className={styles.headerLogo}>
          <Image
            src="/_a/images/amity_logo_c.svg"
            alt="Amity Logo"
            width={124}
            height={44}
            title="Amity Logo"
            priority
            sizes="(max-width: 349px) 86px, 124px"
            decoding="async"
          />
        </Link>
        <div className={styles.headerBtnWrapper}>
          {pathname != "/predegree" && !isCertificationsPage && !isSponsoredCertificates && (
            <ClientSideButton
            modalType={
              ModalTypes.ConnectModal
            }
            GADataNew={{
              ...GA_EVENTS.get(LEAD_INITIATE),
              cta_text: "Enquire Now",
              section_name: "Header",
              header_title: "NA",
              isPageType: true,
              page_url: true,
            }}
            webEngageEvents={{
              "Section Name": "Header",
              isPageType: true,
            }}
            modalData={{
              associatedPrograms: headerMenuData["associatedPrograms"],
              isLandingPage: true,
              popup: popupData,
            }}
            styles={{backgroundColor: "white"}}
            className={styles.enquireNowBtn}
            ctaText={"ENQUIRE NOW"}
            enquiryNow={true}
          />
          )}
          {pathname != "/predegree" && !isCertificationsPage && !isSponsoredCertificates &&(
          <ClientSideButton
            modalType={
              isSponsoredCertificates
                ? ModalTypes.ConnectModalFormSponsoredCourses
                : ModalTypes.ConnectModal
            }
            GADataNew={{
              ...GA_EVENTS.get(LEAD_INITIATE),
              cta_text: "Apply Now",
              section_name: "Header",
              header_title: "NA",
              isPageType: true,
              page_url: true,
            }}
            webEngageEvents={{
              "Section Name": "Header",
              isPageType: true,
            }}
            modalData={{
              associatedPrograms: headerMenuData["associatedPrograms"],
              isLandingPage: true,
              popup: popupData,
            }}
            ctaText={isSponsoredCertificates ? "ENROLL NOW" : "APPLY NOW"}
            className={styles.applyNowBtn}

          />
          )}
           {pathname == "/predegree" && <a
            href="https://predegrees.amityonline.com/login/index.php"
            className={
              "flex items-center border-[1.5px] border-[#1a325d] mr-4 px-3 py-2 text-[#1a325d] bg-[#ffc907] rounded-[50px]"
            }
          >
            <span className="flex items-center">
              <svg style={{ width: '25px', height: '25px;', fill: "#1b325d"}} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 101 101">
                <path d="M50.4 54.5c10.1 0 18.2-8.2 18.2-18.2S60.5 18 50.4 18s-18.2 8.2-18.2 18.2 8.1 18.3 18.2 18.3zm0-31.7c7.4 0 13.4 6 13.4 13.4s-6 13.4-13.4 13.4S37 43.7 37 36.3s6-13.5 13.4-13.5zM18.8 83h63.4c1.3 0 2.4-1.1 2.4-2.4 0-12.6-10.3-22.9-22.9-22.9H39.3c-12.6 0-22.9 10.3-22.9 22.9 0 1.3 1.1 2.4 2.4 2.4zm20.5-20.5h22.4c9.2 0 16.7 6.8 17.9 15.7H21.4c1.2-8.9 8.7-15.7 17.9-15.7z"></path>
              </svg>
              <span className="text-[0.8rem] uppercase font-bold">Login</span>
            </span>
          </a>
          }
          
          <button
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            className={styles.menuToggleBtn}
          >
            {isMenuOpen ? <Close /> : <Hamburger />}
          </button>
        </div>
      </div>
      <MobileMenuOpt
        isSponsoredCertificates={isSponsoredCertificates}
        isMenuOpen={isMenuOpen}
        menuPage={menuPage}
        setMenuPage={setMenuPage}
      />
    </header>
    {!isMenuOpen && !(isCertificationsPage || isSponsoredCertificates) && <div className={styles.homeSearchWrapper}>
          <ProgramSearcher
            searchValue={searchValue}
            setSearchValue={setSearchValue} 
          />
           { searchValue&& <div className={styles.homeSearchResults}>
              <ProgramSearchResults searchValue={searchValue} />
            </div>}
        </div>}
    </>
  );
};

export default React.memo(HeaderOpt);
